import { useState, useRef, useMemo, useEffect, useCallback, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OAuthButtons from '../components/OAuthButtons';
import PasswordInput from '../components/PasswordInput';
import { HelpPopover } from '../components/help';
import { isSafeRedirect } from '../lib/redirectGuard';
import { isInvitationFlowRedirect, matchesInvitationFlowPrefix } from '../lib/invitationFlow';
import { readInvitationSkipParams } from '../lib/invitationSkip';
import { apiFetch, ApiError } from '../lib/api';
import i18n from '../i18n';
import {
  detectCurrency,
  formatTierPrice,
  type PriceableTier,
} from '../lib/currency';

// #2189 — the shared validate middleware's 400 response is
// {error: 'Validation failed', details: [{path, message}, ...]}. Reading
// only err.message (the earlier behaviour) surfaced the generic top-level
// string with no way to tell WHICH field failed or why — the actual defect
// that made this fix's own root cause opaque to diagnose live. Falls back to
// err.message when there's no structured detail to show (e.g. a non-Zod
// AppError like "An account with this email already exists").
function formatRegisterError(err: unknown): string {
  if (err instanceof ApiError) {
    const details = err.body?.details;
    if (Array.isArray(details) && details.length > 0) {
      const messages = details
        .map(d => (d && typeof d === 'object' && 'message' in d ? String((d as { message: unknown }).message) : null))
        .filter((m): m is string => m !== null);
      if (messages.length > 0) return messages.join(' ');
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Registration failed';
}

// #2452 — extracts the raw token from a `/accept-invite?token=<hex>` redirect
// so RegisterPage can pre-fill the invited email before the visitor signs up.
// Deliberately scoped to the bare team-invite path only (not
// /client/accept-invite, /setup/accept, or /academic/accept — those are a
// different service/token model entirely; see GET /invitations/:token/status's
// own comment).
//
// R1 review (#2452) caught an earlier version of this function gating on a
// bare `redirect.startsWith('/accept-invite')` — NOT anchored on the trailing
// boundary, unlike isInvitationFlowRedirect()'s own check. A decoy redirect
// like `/accept-invite-x?token=<real>` failed isInvitationFlowRedirect() (so
// the page rendered the ordinary solo-signup flow) while still passing that
// unanchored check, fetching and LOCKING a real invited email into a page
// that wasn't even in invitation-flow mode. matchesInvitationFlowPrefix()
// reuses isInvitationFlowRedirect()'s own compiled, anchored regex for this
// exact prefix — not a second, independently-written anchoring check — so
// this function's safety no longer depends on `redirect` having already
// passed through a DIFFERENT function's anchoring; it does its own.
//
// Safe to parse with a placeholder base regardless: the anchored match above
// guarantees `redirect` literally starts with '/accept-invite' by the time
// `new URL` runs, so it can never resolve to a different origin.
function readTeamInviteToken(redirect: string | null): string | null {
  if (!redirect || !matchesInvitationFlowPrefix(redirect, '/accept-invite')) return null;
  try {
    return new URL(redirect, 'http://placeholder.invalid').searchParams.get('token');
  } catch {
    return null;
  }
}

function BlueprintMark({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="10" fill="#7C3AED" />
      <path d="M12 32 L24 26 L36 32 L24 38 Z" fill="white" opacity="0.22" />
      <path d="M12 26 L24 20 L36 26 L24 32 Z" fill="white" opacity="0.55" />
      <path d="M12 20 L24 14 L36 20 L24 26 Z" fill="white" opacity="0.9" />
      <circle cx="36" cy="12" r="5" fill="#EC4899" />
    </svg>
  );
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: 44,
  background: 'var(--surface-alt)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 14px',
  fontFamily: 'var(--font-body)',
  fontSize: 14,
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};

// CFG.I18N.A2: Tier price is no longer a hardcoded string — each option
// stores the canonical tier ID and we look up the region-appropriate
// price at render time via formatTierPrice(). The legacy `tierPrice`
// field is gone; anyone reading the old shape will get a type error.
// #1825 — pricing-page plan intent. Mirrors auth.ts's VALID_PLANS
// (independently duplicated, kept in sync by convention — same pattern
// this file already uses for TEAM_SIZE_OPTIONS' orgSize vs. the backend's
// ORG_SIZE_VALUES). Case-insensitive read (AC1); anything else absent or
// unrecognized shows no banner and doesn't touch the default flow (AC2).
const VALID_PLANS = ['individual', 'starter', 'growth', 'platform-partner'] as const;
type PlanIntent = typeof VALID_PLANS[number];
const PLAN_LABELS: Record<PlanIntent, string> = {
  individual:        'Individual',
  starter:           'Starter',
  growth:            'Growth',
  'platform-partner': 'Platform Partner',
};

function readPlanIntent(raw: string | null): PlanIntent | undefined {
  const normalized = raw?.toLowerCase();
  return normalized && (VALID_PLANS as readonly string[]).includes(normalized)
    ? (normalized as PlanIntent)
    : undefined;
}

// SIGNUP.B1 (#2189) D3 — ONE team-size question with five options. `value` is
// the signup_team_size slug from migration 0214:118 (sent as-is and stored on
// users); `orgSize` is the backend's ORG_SIZE_VALUES entry it derives, which
// still drives the tenant tier and the badge below exactly as before.
//
// "Just me" and "2–10" were a single option ('1-10', "Just me / 2–10 people")
// before B1, so splitting them keeps both on orgSize '1-10' — the same tier and
// badge a solo user got yesterday. That is not a new tier mapping. Whether the
// badge should bind the tier at all is #3025; what tier a SKIPPED answer gets
// is open with AVR, so skipping sends no orgSize (see handleStep2).
const TEAM_SIZE_OPTIONS: readonly {
  value: string;
  label: string;
  orgSize: string;
  tierLabel: string;
  tier: PriceableTier;
  enterprise?: boolean;
}[] = [
  { value: 'just-me', label: 'Just me',       orgSize: '1-10',   tierLabel: 'Starter', tier: 'STARTER' },
  { value: '1-10',    label: '2–10 people',   orgSize: '1-10',   tierLabel: 'Starter', tier: 'STARTER' },
  { value: '11-50',   label: '11–50 people',  orgSize: '11-50',  tierLabel: 'Growth',  tier: 'GROWTH'  },
  { value: '51-200',  label: '51–200 people', orgSize: '51-200', tierLabel: 'Growth',  tier: 'GROWTH'  },
  { value: '200+',    label: '200+ people',   orgSize: '200+',   tierLabel: 'Growth',  tier: 'GROWTH', enterprise: true },
];

// #3029 pre-merge (R1/R2/CodeRabbit) — a radio group can't be cleared once a
// choice is made, so the optional question needs an explicit way back out.
// This is a form-local value, NOT a 0214 slug: it is never sent, and choosing
// it submits exactly what an untouched question does (no signup_team_size, no
// orgSize). Kept out of TEAM_SIZE_OPTIONS so it can never derive a tier.
const TEAM_SIZE_OPT_OUT = 'prefer-not-to-say';

// SIGNUP.B1 (#2189) D2 — slugs verbatim from migration 0214:116-117 and
// :122-123, mirrored in auth.ts registerSchema (kept in sync by convention,
// like ORG_SIZE_VALUES). Labels are the PRD's (FR-31, FR-36). Both optional.
const ROLE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'founder-ceo',     label: 'Founder / CEO' },
  { value: 'cto-vp-eng',      label: 'CTO / VP Engineering' },
  { value: 'eng-manager',     label: 'Engineering Manager' },
  { value: 'developer',       label: 'Developer / Engineer' },
  { value: 'product-manager', label: 'Product Manager' },
  { value: 'other',           label: 'Other' },
];
const REFERRAL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'search-engine',     label: 'Search engine' },
  { value: 'linkedin',          label: 'LinkedIn' },
  { value: 'word-of-mouth',     label: 'Word of mouth' },
  { value: 'blueprint-partner', label: 'BluePrint partner' },
  { value: 'conference-event',  label: 'Conference / event' },
  { value: 'other',             label: 'Other' },
];

export default function RegisterPage() {
  const { registerSme, setSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ONB.B2-FIX.D2-FOLLOWON.A1 (#1110) — when the user arrived via the
  // invitation magic-link round-trip (e.g. /register?redirect=/setup/accept?token=...),
  // they're here to JOIN an existing tenant, not create a new one. Skip
  // Step 2 (workspace creation fields) entirely and submit Step 1 directly
  // to /auth/register. The backend detects the same prefix and skips
  // tenant + tenant_members + setup_project creation.
  const isInvitationFlow = isInvitationFlowRedirect(searchParams.get('redirect'));

  // #1825 (AC1) — component state only, NOT localStorage/sessionStorage —
  // this is a one-time signup-moment signal, not something that should
  // persist across sessions if the visitor abandons and comes back later
  // without the param.
  const planIntent = useMemo(() => readPlanIntent(searchParams.get('plan')), [searchParams]);

  // Step state
  const [step, setStep] = useState<1 | 2>(1);

  // Step 1 fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // #2189 (SIGNUP.B1, minimal unblock) — POST /auth/register has required
  // terms_accepted: z.literal(true) since SIGNUP.A3 (#2188), but until this
  // fix no UI anywhere collected or sent it, so every registration 400'd.
  // Unchecked by default; gates both submit paths below.
  const [termsAccepted, setTermsAccepted] = useState(false);
  // SIGNUP.B1 (#2189) D1 — optional marketing consent (FR-10/FR-11). Unchecked
  // by default (opt-in, not opt-out) and deliberately NOT part of
  // canSubmitStep1: it must never gate signup, and terms must never imply it.
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Step 2 fields
  const [orgName, setOrgName] = useState('');
  // #2189 D3 — the signup_team_size slug; '' = not answered, TEAM_SIZE_OPT_OUT =
  // explicitly declined (submitted the same as ''). orgSize is derived from it
  // (TEAM_SIZE_OPTIONS), not held as separate state.
  const [teamSize, setTeamSize] = useState<string>('');
  // #2189 D2 — '' = not answered.
  const [signupRole, setSignupRole] = useState<string>('');
  const [referralSource, setReferralSource] = useState<string>('');
  const [orgNameError, setOrgNameError] = useState('');
  const [orgNameFocused, setOrgNameFocused] = useState(false);
  const orgNameInputRef = useRef<HTMLInputElement>(null);

  // #2452 — pre-fill + lock the Email field from the invited address when the
  // redirect carries a live team-invite token, so the visitor can't waste a
  // signup round-trip typing a different address than the one they were
  // invited on (acceptInvitation()'s existing mismatch check would 403 that
  // later, unchanged). `emailLocked` starts false and only ever flips true —
  // an expired/invalid/already-accepted token just leaves the field blank
  // and editable, the pre-existing behaviour.
  const [emailLocked, setEmailLocked] = useState(false);
  const [checkingInvite, setCheckingInvite] = useState(false);

  const fetchInvitedEmail = useCallback(async () => {
    const token = readTeamInviteToken(searchParams.get('redirect'));
    if (!token) return;
    setCheckingInvite(true);
    try {
      const data = await apiFetch<{ email: string }>(`/invitations/${token}/status`);
      setEmail(data.email);
      setEmailLocked(true);
    } catch {
      // Expired/invalid/already-accepted token — fall back to the normal
      // blank, editable field. Not an error state here: a genuinely bad
      // token still surfaces later via the existing accept-step handling.
    } finally {
      setCheckingInvite(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchInvitedEmail();
  }, [fetchInvitedEmail]);

  const [error, setError] = useState(() => {
    const oauthError = searchParams.get('error');
    const provider = searchParams.get('provider');
    if (oauthError === 'email_exists') return 'An account with this email already exists. Please sign in using your original sign-in method (password, Google, or GitHub).';
    if (oauthError === 'email_required') return `Your ${provider ?? 'social'} account has no public email. Please add one and try again.`;
    if (oauthError === 'oauth_failed') return 'Social sign-in failed. Please try again.';
    // oauth_not_configured: the API has no client for that provider. Before
    // #3039 this code rendered nothing.
    if (oauthError === 'oauth_not_configured') return "Social sign-in isn't available for this provider. Please sign in with your email and password.";
    // #3039 — the API's startup check found this provider's callback pointing
    // at another deployment and disabled it.
    if (oauthError === 'oauth_misconfigured') return 'Social sign-in with this provider is temporarily unavailable because of a server configuration problem. Please sign in with your email and password, or try again later.';
    // #3023 — the API refuses to create an OAuth account without terms consent
    // (a first-time user who clicked Google/GitHub on /login, or a direct hit).
    if (oauthError === 'terms_required') return 'Please accept the Terms of Service and Privacy Policy before continuing with Google or GitHub.';
    return '';
  });
  const [loading, setLoading] = useState(false);

  const selectedTier = TEAM_SIZE_OPTIONS.find(o => o.value === teamSize);
  // #2189 — single source of truth for the Step 1 button's disabled/cursor/
  // opacity/hover logic below, rather than repeating `loading || !termsAccepted`
  // at each of the four sites (R1 review).
  // #2452 (R2/Codex finding) — also requires !checkingInvite: the button was
  // previously enabled during the invite-lookup window even though the email
  // field is disabled-and-blank then, so a fast submit could fire with an
  // empty email. handleStep1 has its own matching code-level guard above,
  // same reasoning as termsAccepted's.
  const canSubmitStep1 = !loading && !checkingInvite && termsAccepted;

  // CFG.I18N.A2: Pick display currency from all available browser-side
  // signals. No tenant exists yet (this is the pre-auth signup page) so
  // we can't use a stored region — browser timezone and locale are the
  // best available signals. Memoised so the tier badge doesn't
  // re-resolve on every input keystroke.
  const displayCurrency = useMemo(() => {
    let timezone: string | null = null;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
      // Some very old/locked-down browsers throw here; fall through to
      // the safe USD default rather than crashing the signup page.
      timezone = null;
    }
    const browserLocale =
      typeof navigator !== 'undefined' ? navigator.language ?? null : null;
    return detectCurrency({
      locale: i18n.language,
      timezone,
      browserLocale,
    });
  }, []);

  const handleStep1 = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    // #2189 — code-level guard, not just the disabled button attribute: a
    // disabled button doesn't reliably block an Enter-key form submit in
    // every browser, and this is the one field the user MUST affirmatively
    // author (see auth.ts's z.literal(true) comment) — silently proceeding
    // without it would record an account with no consent.
    if (!termsAccepted) {
      setError('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }
    // #2452 (R2/Codex finding) — same code-level-guard reasoning as the
    // termsAccepted check above: canSubmitStep1 already disables the button
    // while checkingInvite is true, but a disabled button doesn't reliably
    // block an Enter-key submit in every browser, and a fast submit (or
    // autofill) during the invite-lookup window would otherwise fire with
    // email still blank — the exact wasted round-trip #2452 exists to
    // prevent, not just re-created but self-inflicted by this feature.
    if (checkingInvite) {
      return;
    }

    // ONB.B2-FIX.D2-FOLLOWON.A1 — invitation flow skips Step 2 entirely.
    // POST /auth/register directly with no orgName / orgSize; the backend
    // detects the same prefix and creates user only (no orphan tenant).
    if (isInvitationFlow) {
      const rawRedirect = searchParams.get('redirect');
      const redirect = isSafeRedirect(rawRedirect) ? rawRedirect : undefined;
      // #1975 — forward the invitation token + its path discriminator so the
      // backend can decide whether this registration qualifies to skip email
      // verification. Null for the team path and for any non-invitation
      // redirect, which is the fall-through to ordinary verification.
      const skipParams = readInvitationSkipParams(redirect);
      setLoading(true);
      try {
        const result = await registerSme({
          name, email, password, redirect, plan: planIntent, terms_accepted: termsAccepted,
          marketing_opt_in: marketingOptIn,
          ...(skipParams ?? {}),
        });
        if (result.requiresVerification) {
          const verifyUrl = redirect
            ? `/verify-email?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirect)}`
            : `/verify-email?email=${encodeURIComponent(email)}`;
          navigate(verifyUrl);
        } else if (result.token) {
          // #1975 — the backend validated the invitation token and skipped
          // verification. Store the (tenant-less) session and go straight to
          // the accept page, which consumes the invitation, flips
          // email_verified, and hands back a tenant-scoped token. No second
          // email, no inbox round-trip.
          // setSession, not setAuthFromToken — we have the real name and
          // email right here from the form, whereas the JWT carries only
          // userId. Seeding them now means the accept step's own
          // setAuthFromToken (which preserves identity for the same user)
          // carries them forward instead of leaving the whole session with a
          // blank name/email.
          setSession(result.token, { id: result.userId, email, name });
          navigate(redirect ?? '/dashboard', { replace: true });
        }
      } catch (err) {
        setError(formatRegisterError(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Solo flow: existing two-step behavior.
    // Entering Step 2 fresh — clear any stale validation errors from a
    // previous visit (e.g. user clicked Back, edited Step 1, and returned).
    setOrgNameError('');
    setStep(2);
  };

  // Inline validation for Step 2 — fixes silent failure where the submit button
  // was disabled on empty required fields, giving users no feedback.
  const validateOrgName = (value: string): string => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return 'Organisation name is required';
    if (trimmed.length < 2) return 'Organisation name must be at least 2 characters';
    if (trimmed.length > 100) return 'Organisation name must be less than 100 characters';
    return '';
  };

  // #2189 D3 — team size is OPTIONAL (ruled on #2189), so it has no validator.
  // Organisation name remains the only required Step 2 field (FR-30).
  const handleStep2 = async (e: FormEvent) => {
    e.preventDefault();

    const nameError = validateOrgName(orgName);

    if (nameError) {
      setOrgNameError(nameError);
      // Focus the invalid control so keyboard/screen-reader users land on it.
      orgNameInputRef.current?.focus();
      return;
    }

    setLoading(true);
    setError('');
    try {
      // ONB.B2-FIX.D2 — when the user arrived via a magic-link round-trip,
      // forward the redirect target through registerSme (which threads it
      // into the POST /auth/register body) so the backend can embed it
      // into the verification email URL. The frontend isSafeRedirect guard
      // here is defense-in-depth; the backend's equivalent server-side
      // guard is the authoritative gate.
      const rawRedirect = searchParams.get('redirect');
      const redirect = isSafeRedirect(rawRedirect) ? rawRedirect : undefined;

      // #2189 D2/D3 — unanswered optional fields are sent as undefined (so
      // JSON.stringify omits them), never as ''. An empty string is not a slug:
      // the API would drop it anyway, but for orgSize it is worse — orgSize is
      // a strict z.enum, so '' would 400 the whole signup. A skipped team size
      // therefore sends NO orgSize, and the tier falls to whatever the API
      // already does for an absent orgSize (auth.ts, unchanged by B1).
      // signup_team_size comes from the same selectedTier lookup as orgSize,
      // so TEAM_SIZE_OPT_OUT (not in TEAM_SIZE_OPTIONS) sends neither.
      const result = await registerSme({
        name, email, password, orgName: orgName.trim(),
        orgSize: selectedTier?.orgSize,
        redirect, plan: planIntent, terms_accepted: termsAccepted, marketing_opt_in: marketingOptIn,
        signup_team_size:       selectedTier?.value,
        signup_role:            signupRole || undefined,
        signup_referral_source: referralSource || undefined,
      });
      if (result.requiresVerification) {
        // Cross-device resilience: redirect rides on the URL through the SPA's
        // /verify-email landing so that if the user clicks the verification
        // email from a different device than they registered on, the
        // server-side redirect plumbing still routes them to the intended
        // destination after they exchange the verification code for a JWT.
        const verifyUrl = redirect
          ? `/verify-email?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirect)}`
          : `/verify-email?email=${encodeURIComponent(email)}`;
        navigate(verifyUrl);
      }
    } catch (err) {
      setError(formatRegisterError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg)' }}>
      <div style={{
        width: '100%',
        maxWidth: 440,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-lg)',
        padding: 40,
      }}>
        {/* Brand header */}
        <div className="flex flex-col items-center mb-8">
          <BlueprintMark size={48} />
          <div className="flex items-baseline mt-3">
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Blue</span>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--primary)', letterSpacing: '-0.5px' }}>Print</span>
          </div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
            {isInvitationFlow
              ? 'Sign up to accept your invitation'
              : step === 1 ? 'Create your account' : 'Set up your workspace'}
          </p>
        </div>

        {/* #1825 (AC2) — plan-intent chip. Only renders for a valid,
            recognized ?plan= — absent/invalid shows nothing, default
            Individual experience unchanged. */}
        {planIntent && (
          <div style={{
            textAlign: 'center',
            marginBottom: 20,
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--violet-l)',
          }}>
            You&apos;re signing up for the {PLAN_LABELS[planIntent]} plan
          </div>
        )}

        {/* Step indicator — collapses to a single dot in invitation flow
            (no Step 2 to render). Paste-back #4 option β: conditional
            array literal makes the intent visible at the literal level. */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
          {(isInvitationFlow ? [1] : [1, 2]).map(s => (
            <div key={s} style={{
              width: 8, height: 8, borderRadius: '50%',
              background: s <= step ? 'var(--primary)' : 'var(--border)',
              transition: 'background 200ms',
            }} />
          ))}
        </div>

        {error && (
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', marginBottom: 16 }}>
            {error}
          </div>
        )}

        {step === 1 ? (
          <form onSubmit={handleStep1}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                Full Name
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                readOnly={emailLocked}
                disabled={checkingInvite}
                style={{
                  ...inputStyle,
                  ...(emailLocked ? { background: 'var(--surface)', color: 'var(--text-secondary)', cursor: 'not-allowed' } : {}),
                }}
                onFocus={e => { if (!emailLocked) { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; } }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
              {/* #2452 — tells the visitor WHY the field they didn't type into
                  is already filled in, rather than looking like a stray
                  autofill. Only rendered once a live invite has actually
                  resolved. */}
              {emailLocked && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  This is the address your invitation was sent to.
                </p>
              )}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                Password
              </label>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>
                Confirm Password
              </label>
              <PasswordInput
                required
                minLength={8}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.12)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 24, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={e => setTermsAccepted(e.target.checked)}
                // Explicit label, not just the adjacent <span> — keeps this
                // selector stable (getByRole('checkbox', {name: ...})) once a
                // second checkbox (e.g. marketing_opt_in, optional per
                // auth.ts) is added here, per R1 review.
                aria-label="I agree to the Terms of Service and Privacy Policy"
                style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16 }}
              />
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                I agree to the{' '}
                {/* #2505 — these anchors sit inside the <label>, so the question is whether
                    clicking one also toggles consent. Per HTML spec it does not: a label's
                    activation behaviour 'for events targeted at interactive content
                    descendants ... must be to do nothing'. Evergreen browsers implement that.

                    RESIDUAL RISK, ACCEPTED RATHER THAN MITIGATED (R1, #2505). happy-dom does
                    NOT implement the exclusion, so a low-fidelity engine — an old in-app
                    WebView, say — could plausibly toggle consent on a link click. There is no
                    guard here because none works: probed against happy-dom with no handler,
                    with onClick stopPropagation, and with nativeEvent.stopImmediatePropagation,
                    and the checkbox toggles in all three. The mechanism is native label
                    forwarding, not event bubbling, so a propagation guard cannot reach it.
                    Moving the anchors outside the <label> would, at the cost of the whole
                    sentence no longer being clickable. Not asserted in the unit suite for the
                    same reason — see RegisterPageTermsConsent.test.tsx. */}
                <a
                  href="https://blueprintsdlc.com/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://blueprintsdlc.com/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </a>
              </span>
            </label>

            {/* SIGNUP.B1 (#2189) D1 — marketing consent, below terms and visually
                distinct from it (FR-10, NFR-06). A separate <label>/<input> with
                its own state: the DPDP Act separation requirement is that consent
                to the contract is never consent to marketing. The negative top
                margin pulls it under the terms row without touching that row's
                markup, which #2442/#2505 shipped and this story leaves as-is. */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: -12, marginBottom: 24, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={e => setMarketingOptIn(e.target.checked)}
                aria-label="Send me product updates, feature announcements, and tips (optional)"
                style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16 }}
              />
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                Send me product updates, feature announcements, and tips (optional)
              </span>
            </label>

            {/* ONB.B2-FIX.D2-FOLLOWON.A1 R2 — invitation flow makes handleStep1
                a network call (registerSme), so we must disable the button +
                show loading text during flight. Solo flow keeps the original
                "Continue" label because handleStep1 there is a synchronous
                advance to Step 2 (no loading state needed). */}
            <button
              type="submit"
              disabled={!canSubmitStep1}
              style={{
                display: 'block', width: '100%', height: 44,
                background: 'var(--primary)', color: 'white',
                fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
                borderRadius: 'var(--radius-sm)', border: 'none',
                cursor: canSubmitStep1 ? 'pointer' : 'not-allowed',
                opacity: canSubmitStep1 ? 1 : 0.7,
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { if (canSubmitStep1) e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              {checkingInvite ? 'Checking invitation…' : loading && isInvitationFlow ? 'Signing up…' : 'Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleStep2}>
            <div style={{ marginBottom: 16 }}>
              <label
                htmlFor="orgName"
                style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
              >
                Organisation Name
              </label>
              <input
                id="orgName"
                ref={orgNameInputRef}
                type="text"
                value={orgName}
                onChange={e => {
                  setOrgName(e.target.value);
                  if (orgNameError) setOrgNameError('');
                }}
                placeholder="e.g. Acme Inc."
                aria-invalid={orgNameError ? true : undefined}
                aria-describedby={orgNameError ? 'orgName-error' : undefined}
                style={{
                  ...inputStyle,
                  // Border and focus ring derived from state so they stay in sync
                  // if the error clears while the field is still focused.
                  borderColor: orgNameError
                    ? 'var(--error)'
                    : orgNameFocused
                    ? 'var(--primary)'
                    : 'var(--border)',
                  boxShadow:
                    orgNameFocused && !orgNameError
                      ? '0 0 0 3px rgba(124,58,237,0.12)'
                      : 'none',
                }}
                onFocus={() => setOrgNameFocused(true)}
                onBlur={() => setOrgNameFocused(false)}
              />
              {orgNameError && (
                <p
                  id="orgName-error"
                  role="alert"
                  style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 4 }}
                >
                  {orgNameError}
                </p>
              )}
            </div>

            <fieldset
              style={{
                // Reset default fieldset styling so we own all visuals.
                border: 'none',
                padding: 0,
                margin: 0,
                marginBottom: 16,
                minInlineSize: 'auto',
              }}
            >
              <legend
                style={{ display: 'block', padding: 0, fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 10 }}
              >
                Team Size (optional)
              </legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {TEAM_SIZE_OPTIONS.map(opt => {
                  const selected = teamSize === opt.value;
                  const borderColor = selected ? 'var(--primary)' : 'var(--border)';
                  return (
                    <label
                      key={opt.value}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px',
                        border: `2px solid ${borderColor}`,
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        background: selected ? 'rgba(124,58,237,0.04)' : 'transparent',
                        transition: 'border-color 150ms, background 150ms',
                      }}
                    >
                      <input
                        type="radio"
                        name="teamSize"
                        value={opt.value}
                        checked={selected}
                        onChange={e => setTeamSize(e.target.value)}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', flex: 1 }}>
                        {opt.label}
                      </span>
                    </label>
                  );
                })}
                {/* #3029 — opt-out, last and visually secondary: dashed border,
                    muted text, no selected tint, so it reads as declining the
                    question rather than as a sixth team size. */}
                <label
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '8px 14px',
                    marginTop: 4,
                    border: `1px dashed ${teamSize === TEAM_SIZE_OPT_OUT ? 'var(--text-secondary)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    background: 'transparent',
                    transition: 'border-color 150ms',
                  }}
                >
                  <input
                    type="radio"
                    name="teamSize"
                    value={TEAM_SIZE_OPT_OUT}
                    checked={teamSize === TEAM_SIZE_OPT_OUT}
                    onChange={e => setTeamSize(e.target.value)}
                  />
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
                    Prefer not to say
                  </span>
                </label>
              </div>
            </fieldset>

            {/* Dynamic tier badge */}
            {selectedTier && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', marginBottom: 20,
                background: 'rgba(124,58,237,0.06)',
                border: '1px solid rgba(124,58,237,0.15)',
                borderRadius: 'var(--radius-sm)',
              }}>
                <span style={{
                  display: 'inline-block', padding: '2px 8px',
                  background: 'var(--primary)', color: 'white',
                  fontSize: 11, fontWeight: 600, borderRadius: 4,
                  fontFamily: 'var(--font-display)',
                }}>
                  {selectedTier.tierLabel}
                </span>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                  {selectedTier.tierLabel} plan recommended — {formatTierPrice(selectedTier.tier, displayCurrency)}
                </span>
                <HelpPopover
                  id="tier-selection"
                  heading="Choosing your tier"
                  body={
                    <>
                      <p style={{ margin: '0 0 8px' }}>
                        Your tier sets your monthly token limit and which capabilities you can use.
                      </p>
                      <p style={{ margin: '0 0 6px' }}>
                        <strong>INDIVIDUAL</strong> — free, 1M tokens/month, single user, core capabilities. Best for evaluation and solo projects.
                      </p>
                      <p style={{ margin: '0 0 6px' }}>
                        <strong>STARTER</strong> — paid, higher token limit, multiple users, full capability catalogue. Best for small teams.
                      </p>
                      <p style={{ margin: '0 0 6px' }}>
                        <strong>GROWTH</strong> — paid, higher limits and capacity. Best for active multi-project teams.
                      </p>
                      <p style={{ margin: '0 0 6px' }}>
                        <strong>ENTERPRISE</strong> — sales-led, custom limits, dedicated support, advanced governance.
                      </p>
                      <p style={{ margin: '0 0 8px' }}>
                        <strong>PLATFORM_PARTNER</strong> — for SI partners managing multiple tenants. Contact us if this fits.
                      </p>
                      <p style={{ margin: 0 }}>
                        You can upgrade at any time. Downgrades take effect at the next billing cycle.
                      </p>
                    </>
                  }
                />
                {selectedTier.enterprise && (
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--primary)', marginLeft: 'auto' }}>
                    Enterprise? Contact sales
                  </span>
                )}
              </div>
            )}

            {/* SIGNUP.B1 (#2189) D2 — optional B2B signals (FR-31, FR-36).
                Informational only: stored on users, never read for tier or
                access. The empty option means "not answered" and is sent as
                nothing, never as ''. */}
            <div style={{ marginBottom: 16 }}>
              <label
                htmlFor="signupRole"
                style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
              >
                Your role (optional)
              </label>
              <select
                id="signupRole"
                value={signupRole}
                onChange={e => setSignupRole(e.target.value)}
                style={inputStyle}
              >
                <option value="">Select…</option>
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label
                htmlFor="signupReferralSource"
                style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
              >
                How did you hear about us? (optional)
              </label>
              <select
                id="signupReferralSource"
                value={referralSource}
                onChange={e => setReferralSource(e.target.value)}
                style={inputStyle}
              >
                <option value="">Select…</option>
                {REFERRAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                display: 'block', width: '100%', height: 44,
                background: 'var(--primary)', color: 'white',
                fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600,
                borderRadius: 'var(--radius-sm)', border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                transition: 'box-shadow 150ms',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.boxShadow = 'var(--shadow-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              {loading ? 'Creating workspace…' : 'Create my workspace'}
            </button>

            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setStep(1)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)',
                  padding: 0,
                }}
              >
                Back
              </button>
            </div>
          </form>
        )}

        {/* Divider */}
        <div className="flex items-center" style={{ margin: '20px 0', gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        </div>

        {/* #3023 — the terms checkbox above gates OAuth signup too, not only the
            password submit: the buttons stay disabled until it is ticked, then
            start the provider flow with ?terms=accepted. */}
        <OAuthButtons termsAccepted={termsAccepted} />

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 20 }}>
          Already have an account?{' '}
          <Link
            to={(() => {
              // ONB.B2-FIX.D2 — symmetric to LoginPage's "Sign up" link.
              // Forward ?redirect= so the magic-link round-trip survives
              // a user toggling between auth pages.
              const redirect = searchParams.get('redirect');
              return isSafeRedirect(redirect)
                ? `/login?redirect=${encodeURIComponent(redirect)}`
                : '/login';
            })()}
            style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
          >
            Sign in
          </Link>
        </p>

        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 32, opacity: 0.6 }}>
          &copy; {new Date().getFullYear()} BluePrint &middot; Built with &#9829; by Ambai
        </p>
      </div>
    </div>
  );
}
